/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import eventImage from "../../../assets/images/no_image_placeholder.jpg";
import PopUpModal from "../../PopUpModal/PopUpModal";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useCreateEventContext } from "../../../context/CreateEventContext";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";
import ProfileContent from "../Settings/ProfileContent";
import EventDetails from "./ViewEventDetails/EventDetails";
import Faq from "./ViewEventDetails/Faq";
import Announcements from "./ViewEventDetails/Announcements";
import { StatusBadge } from "../../ui/TableComponents";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/solid";
import ModalComponent from "./EventsModal";
import { withdrawMPSApplication } from "../../../firebaseFunctions/getMPSApplicationDetails";
import MyDetails from "./ViewEventDetails/MyDetails";
import EventDocuments from "./ViewEventDetails/EventDocuments";
import MyBooth from "./ViewEventDetails/MyBooth";
import Button from "../../ui/Button";
import { loadStripe } from "@stripe/stripe-js";
import { getFunctions, httpsCallable } from "firebase/functions";
import { firestore } from "../../../firebase";
import { CardElement, useElements, Elements } from "@stripe/react-stripe-js";
import { receiptService } from "../../../services/ReceiptService";
import { useAuth } from "../../../context/AuthContext";
import ReceiptViewer from "./ReceiptViewer";
import {
  getEventRequiredDocuments,
  getVendorAcknowledgment,
  getVendorSignature,
} from "../../../services/EventDocumentService";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const BASE_TABS = [
  "Event Details",
  "My Details",
  "Announcements",
  "FAQ",
  "Event Documents",
];

const ViewEventDetailsNew = () => {
  const navigate = useNavigate();
  const eventId = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(null);
  const { getEventData, eventData } = useCreateEventContext();
  const [popUpModalType, setPopUpModalType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [pendingDocCount, setPendingDocCount] = useState(0);

  const isPaid = eventData?.paymentStatus?.toLowerCase() === "paid";
  const isRegistered = isPaid && (
    eventData?.applicationStatus === "Registered" ||
    eventData?.applicationStatus === "Approved"
  );
  const tabs = isRegistered
    ? ["My Booth", ...BASE_TABS]
    : BASE_TABS;

  const renderContent = () => {
    switch (selectedTab) {
      case "My Booth":
        return <MyBooth />;
      case null:
        return null;
      case "Event Details":
        return (
          <EventDetails
            onWithdraw={() => {
              setPopUpModalType("withdrawApplication");
              setIsModalOpen(true);
            }}
          />
        );
      case "My Details":
        return <MyDetails />;
      case "Announcements":
        return <Announcements />;
      case "FAQ":
        return <Faq />;
      case "Event Documents":
        return <EventDocuments />;
      default:
        return <ProfileContent />;
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleWithdrawApplication = async () => {
    setIsLoading(true);
    try {
      const response = await withdrawMPSApplication(eventData?.applicationId);
      if (response.success) {
        setPopUpModalType("withdrawSuccess");
      } else {
        setPopUpModalType("withdrawFailure");
      }
    } catch (error) {
      console.error(error);
      setPopUpModalType("withdrawFailure");
    } finally {
      setIsLoading(false);
    }
  };

  const renderCreateEventModal = () => {
    switch (popUpModalType) {
      case "withdrawApplication":
        return (
          <ModalComponent
            title="Withdraw Application"
            message={`You are about to withdraw your application to ${eventData?.eventName}. You will be removed from the event, and any fees paid will be refunded per the event's policies.`}
            secondMessage="This action can't be undone."
            checkboxLabel="Yes, update these changes to my profile"
            firstButtonLabel="Withdraw Application"
            onFirstButtonClick={handleWithdrawApplication}
            secondButtonLabel="No, Go Back"
            secondButtonVariant="transparent"
            onSecondButtonClick={() => {
              setPopUpModalType(null);
              setIsModalOpen(false);
            }}
          />
        );
      case "withdrawSuccess":
        return (
          <ModalComponent
            icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
            title="You have withdrawn from the event."
            message="Any refunds will be returned to you per the event's policies."
            firstButtonLabel="Return to Dashboard"
            onFirstButtonClick={() => navigate("/mps/events")}
          />
        );
      case "withdrawFailure":
        return (
          <ModalComponent
            title="Error withdrawing your application"
            firstButtonLabel="Withdraw Application"
            onFirstButtonClick={handleWithdrawApplication}
            secondButtonLabel="No, Go Back"
            secondButtonVariant="transparent"
            onSecondButtonClick={() => {
              setPopUpModalType(null);
              setIsModalOpen(false);
            }}
          />
        );
      default:
        return <ModalLoadingState />;
    }
  };

  useEffect(() => {
    if (eventId.id) {
      const fetchEvent = async () => {
        setIsLoading(true);
        try {
          await getEventData(eventId.id);
        } catch (error) {
          console.log("error fetching event", error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchEvent();
    }
  }, [eventId.id, getEventData]);

  // Default tab: honour ?tab= param, then My Booth for registered, else Event Details
  useEffect(() => {
    if (!isLoading && selectedTab === null) {
      const params = new URLSearchParams(location.search);
      const tabParam = params.get("tab");
      const TAB_MAP = {
        announcements: "Announcements",
        booth: "My Booth",
        details: "Event Details",
        documents: "Event Documents",
        faq: "FAQ",
      };
      const fromParam = tabParam ? TAB_MAP[tabParam.toLowerCase()] : null;
      setSelectedTab(fromParam || (isRegistered ? "My Booth" : "Event Details"));
    }
  }, [isLoading, isRegistered, selectedTab, location.search]);

  // Load pending required-doc count for tab badge
  useEffect(() => {
    if (!user?.uid || !eventId.id) return;
    const checkPending = async () => {
      try {
        const result = await getEventRequiredDocuments(eventId.id);
        if (!result.success) return;
        const requiredDocs = result.documents.filter(
          (d) => d.isRequired && d.requiresAcknowledgment !== false
        );
        let count = 0;
        for (const rdoc of requiredDocs) {
          let done = false;
          if (rdoc.requiresSignature && rdoc.signatureFields?.length > 0) {
            const sigResult = await getVendorSignature(eventId.id, rdoc.id, user.uid);
            done = sigResult.signed;
          } else {
            const ackResult = await getVendorAcknowledgment(eventId.id, rdoc.id, user.uid);
            done = ackResult.acknowledged;
          }
          if (!done) count++;
        }
        setPendingDocCount(count);
      } catch (_) {
        // silently ignore
      }
    };
    checkPending();
  }, [user?.uid, eventId.id]);

  const handleBack = () => {
    navigate("/mps/events");
  };

  const handleApplyToEvent = () => {
    navigate(`/mps/event-registration/${eventId.id}`);
  };

  // const handlePayment = async () => {
  //   if (!showPaymentForm) {
  //     setShowPaymentForm(true);
  //     return;
  //   }

  //   setProcessing(true);
  //   setPaymentError(null);

  //   try {
  //     const registrationRef = firestore.collection("mpsEventRegistration").doc(eventData.id);
  //     const registrationDoc = await registrationRef.get();
  //     const eventId = registrationDoc.data().eventId;
  //     const eventDoc = await firestore.collection("events").doc(eventId).get();
  //     const userId = eventDoc.data().userId;
  //     const userDoc = await firestore.collection("users").doc(userId).get();
  //     const stripeUserId = userDoc.data().stripeUserId;

  //     const createPaymentIntent = httpsCallable(getFunctions(), "createConnectedPaymentIntent");
  //     const { data: { clientSecret, paymentIntentId } } = await createPaymentIntent({
  //       amount: 1000,
  //       stripeUserId: stripeUserId
  //     });

  //     const stripe = await stripePromise;
  //     const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
  //       payment_method: { card: elements.getElement(CardElement) },
  //     });

  //     if (error) throw error;

  //     // Update registration status
  //     await registrationRef.update({
  //       paymentStatus: "paid",
  //       paidAt: new Date(),
  //     });

  //     // Generate receipt
  //     const receiptData = {
  //       userId: user.uid,
  //       eventId: eventId,
  //       registrationId: eventData.id,
  //       amount: 1000, // Amount in cents
  //       transactionId: paymentIntentId,
  //       businessName: user.businessName,
  //       eventName: eventData.eventName
  //     };

  //     const receipt = await receiptService.generateReceipt(receiptData);

  //     if (receipt.success) {
  //       // Show success message with receipt download link
  //       setSuccessMessage({
  //         text: "Payment successful! Your receipt has been generated.",
  //         receiptUrl: receipt.downloadUrl
  //       });
  //     }

  //     console.log("Payment and receipt generation successful!");
  //   } catch (error) {
  //     console.error("Payment error:", error);
  //     setPaymentError(error.message || "Payment processing failed");
  //   } finally {
  //     setProcessing(false);
  //   }
  // };
  const loggingData = async () => {
    console.log("eventData?.eventId", eventData?.eventId);
    console.log("eventData?.registrationFee", eventData?.registrationFee);
    console.log("EO ID", eventData?.userId);
    console.log("MPS ID", user.uid);
  };

  const handlePayment = async () => {
    if (processing) return;
    setProcessing(true);
    setPaymentError(null);
    try {
      const fns = getFunctions();
      const createVendorCheckoutSession = httpsCallable(fns, "createVendorCheckoutSession");
      const { data } = await createVendorCheckoutSession({ applicationId: eventData?.applicationId });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setPaymentError("Could not start checkout. Please try again.");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setPaymentError(error.message || "Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <>
      <PopUpModal
        open={isModalOpen}
        handleClose={handleClose}
        popUpModalType={popUpModalType}
      >
        {!isLoading ? renderCreateEventModal() : <ModalLoadingState />}
      </PopUpModal>
      <div className="w-full p-4 sm:p-8">
        <div className="flex flex-col items-center justify-center w-full rounded-2xl">
          <div className="flex items-center justify-between w-full pl-1 pr-1 sm:pr-8 sm:pl-8 ">
            <div className="">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 mb-6 text-sm font-medium underline text-grey-900 underline-offset-4"
              >
                <ion-icon name="chevron-back-outline" />
                Back
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between w-full pl-1 pr-1 sm:pr-8 sm:pl-8">
            <img
              src={eventData?.eventCoverPhoto || eventImage}
              alt="Event"
              className="w-full h-60 object-cover object-middle rounded-t-xl"
            />
          </div>
          <div
            className={`flex flex-col sm:flex-row items-start sm:items-center ${
              eventData.applicationStatus
                ? "justify-start gap-4"
                : "justify-between"
            } w-full pl-1 pr-1 sm:pr-8 py-5 sm:pl-8`}
          >
            <h1 className="text-2xl font-medium text-grey-900">
              {eventData.eventName}
            </h1>
            {eventData?.applicationStatus ? (
              <StatusBadge
                type="application"
                status={eventData.applicationStatus}
                className="max-w-[120px]"
              />
            ) : (
              <button
                onClick={handleApplyToEvent}
                className="px-4 py-3 text-sm font-semibold rounded-lg bg-grey-700 hover:bg-grey-800 text-grey-25"
              >
                Apply To Event
              </button>
            )}
            {(eventData.applicationStatus === "Registered" ||
              eventData.applicationStatus === "Approved") && (
              <div className="flex flex-col items-end gap-2">
                <Button
                  onClick={handlePayment}
                  disabled={processing || isPaid}
                  className="px-4 py-3 text-sm font-semibold w-auto"
                >
                  {processing
                    ? "Redirecting to checkout…"
                    : isPaid
                    ? "Payment Completed"
                    : "Pay Registration"}
                </Button>
                {paymentError && (
                  <div className="text-red-600 text-sm">{paymentError}</div>
                )}
              </div>
            )}
          </div>

          <div className="self-stretch bg-white flex flex-col items-start justify-start w-full pl-1 pr-1 sm:pr-8 py-5 sm:pl-8 ">
            <div className="flex flex-row items-start justify-start px-4 py-2 box-border text-center text-sm text-[#344054] overflow-x-auto">
              <div className="self-stretch flex flex-row items-start justify-start gap-4 flex-nowrap">
                {tabs.map((tab) => (
                  <div
                    key={tab}
                    className={`h-6 flex flex-row items-center justify-center p-2 box-border cursor-pointer whitespace-nowrap ${
                      selectedTab === tab
                        ? "text-[#622020] underline underline-offset-4 font-semibold"
                        : ""
                    }`}
                    onClick={() => setSelectedTab(tab)}
                  >
                    <div className="relative leading-5 flex items-center gap-1.5">
                      {tab}
                      {tab === "Event Documents" && pendingDocCount > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold bg-primary-600 text-white rounded-full leading-none">
                          {pendingDocCount}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="self-stretch bg-white flex flex-col items-start justify-start gap-4">
            {renderContent()}
          </div>

          {successMessage && (
            <ReceiptViewer receiptUrl={successMessage.receiptUrl} />
          )}
        </div>
      </div>
    </>
  );
};

export default function WrappedViewEventDetailsNew() {
  return (
    <Elements stripe={stripePromise}>
      <ViewEventDetailsNew />
    </Elements>
  );
}
